import React from 'react';
import InfoTooltip from '../InfoTooltip';
import GeneralChart from '../GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';

const DEFAULT_COLORS = [
  '#df7838',
  '#4ca38f',
  '#409ec8',
  '#6792f0',
  '#e867a8',
  '#9288ee',
  '#c08a38',
  '#ea7ccc',
  '#949494',
];

const IndustryRatio = () => {
  const chartRef = React.useRef(null);
  const { market_share: marketShare } = React.useContext(CityContext);

  const rangeData = React.useMemo(() => {
    const totalCount = marketShare
      .map(({ count }) => count)
      .reduce((a, b) => a + b, 0);

    return marketShare.map(({ name, id, count }) => {
      return { name, id, ratio: Math.round((count / totalCount) * 100) };
    });
  }, [marketShare]);

  const config = React.useMemo<ECOption>(() => {
    const seoInfo = `截止当前数据显示，【${rangeData[0].name}与${rangeData[1].name}领域】是人才需求的绝对主力，合计占据了${rangeData[0].ratio + rangeData[1].ratio}%的市场份额，反映出这些行业正处于快速扩张或人员流动较高的阶段。`;

    return {
      legend: {
        show: false,
      },
      grid: {
        show: false,
        tooltip: { show: false },
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      color: DEFAULT_COLORS,
      series: [
        {
          type: 'pie',
          silent: true,
          radius: [20, 140],
          roseType: 'radius',
          center: ['50%', '50%'],
          itemStyle: {
            borderRadius: 5,
          },
          label: {
            show: true,
            backgroundColor: 'transparent',
            bleedMargin: 1,
          },
          labelLine: { length: 0 },
          emphasis: {
            disabled: true,
            label: {
              show: false,
            },
          },
          cursor: 'default',
          data: rangeData.map(({ name, ratio }) => {
            return { name, value: ratio };
          }),
        },
      ],
      seoInfo,
    };
  }, [rangeData]);

  return (
    <GeneralChart
      title="热门行业人才需求分布"
      subTip="掌握招聘需求最旺盛的领域，找准机会更多的赛道。"
    >
      <div className="tablet:flex tablet:items-center tablet:justify-between">
        <div className="tablet:w-7/12 tablet:shrink-0">
          <div className="w-full h-[290px]">
            <CusEcharts chartRef={chartRef} config={config} />
          </div>
        </div>
        <div className="grid gap-y-2.5 text-xs">
          {rangeData.map(({ name, ratio }, index) => (
            <p
              key={index}
              className="px-10 py-2 text-black/85 flex items-center"
            >
              <span
                className="!leading-5 px-2 rounded-full text-white mr-4 shrink-0"
                style={{ backgroundColor: DEFAULT_COLORS[index] }}
              >
                {index + 1}
              </span>
              <span className="line-clamp-1 w-20 mr-4" title={name}>
                {name}
              </span>
              <span className="ml-auto text-sm shrink-0">{`${ratio}%`}</span>
            </p>
          ))}
        </div>
      </div>
      <p className="flex mt-4">
        <InfoTooltip className="mt-[5px] mr-1 shrink-0" />
        {config.seoInfo}
      </p>
    </GeneralChart>
  );
};

export default IndustryRatio;
